// -----------------------------------------------------------------------------
// Date format rule for the whole app: DD/Mmm/YY (e.g. 03/Jan/26).
// The database stores ISO dates ('2026-01-03'); convert only at the edges.
// -----------------------------------------------------------------------------
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** '2026-01-03' (or a full timestamp) → '03/Jan/26'. Empty/null → '–'. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "–";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "–";
  return `${String(d).padStart(2, "0")}/${MONTHS[m - 1]}/${String(y % 100).padStart(2, "0")}`;
}

/** '03/Jan/26' → '2026-01-03'. Returns null when the text is not a valid date. Years are 20YY. */
export function parseDate(text: string): string | null {
  const m = /^\s*(\d{1,2})\/([A-Za-z]{3})\/(\d{2})\s*$/.exec(text);
  if (!m) return null;
  const month = MONTHS.findIndex((x) => x.toLowerCase() === m[2].toLowerCase()) + 1;
  const day = Number(m[1]), year = 2000 + Number(m[3]);
  if (!month) return null;
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) return null;   // e.g. 31/Feb
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Today as ISO date in the user's timezone. */
export function todayIso(now = new Date()): string {
  const off = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - off).toISOString().slice(0, 10);
}

// The app's "today" (from me(); frozen at 26/Sep/26 in the demo copy). The calendar's Today button uses it.
let appToday: string | null = null;
export function setAppToday(iso: string | null | undefined): void { appToday = iso ?? null; }
export function currentToday(): string { return appToday ?? todayIso(); }

const isoOf = (d: number, m: number, y: number): string | null => {
  if (!(m >= 1 && m <= 12 && d >= 1 && y >= 2000 && y <= 2099)) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};
const yearOf = (t: string): number | null => t.length === 2 ? 2000 + Number(t) : t.length === 4 ? Number(t) : null;

/**
 * Forgiving date reader for typing (round 18 #2) — always day, month, year:
 *   '05/Mar/26' · '050326' · '05032026' · '5326' · '5 3 26' · '5-3-2026' · '5 mar 26' · '5mar26' → '2026-03-05'.
 * 5 digits are read only when just one split makes a real date ('15326' → 15/3/26; '11226' is unclear → null).
 * Returns null when the text can't be read as one real date.
 */
export function parseDateLoose(text: string): string | null {
  const strict = parseDate(text);
  if (strict) return strict;
  const t = text.trim();
  if (!t || /[^0-9A-Za-z\s/.\-,]/.test(t)) return null;
  const parts = t.match(/\d+|[A-Za-z]+/g) ?? [];
  if (parts.length === 1 && /^\d+$/.test(parts[0])) {
    const s = parts[0], n = (a: number, b: number) => Number(s.slice(a, b));
    if (s.length === 4) return isoOf(n(0, 1), n(1, 2), 2000 + n(2, 4));
    if (s.length === 6) return isoOf(n(0, 2), n(2, 4), 2000 + n(4, 6));
    if (s.length === 8) return isoOf(n(0, 2), n(2, 4), n(4, 8));
    if (s.length === 5) {
      const a = isoOf(n(0, 1), n(1, 3), 2000 + n(3, 5)), b = isoOf(n(0, 2), n(2, 3), 2000 + n(3, 5));
      return a && b ? null : a ?? b;
    }
    return null;
  }
  if (parts.length !== 3 || !/^\d{1,2}$/.test(parts[0]) || !/^\d+$/.test(parts[2])) return null;
  const y = yearOf(parts[2]);
  let m: number;
  if (/^\d{1,2}$/.test(parts[1])) m = Number(parts[1]);
  else if (/^[A-Za-z]{3,}$/.test(parts[1])) m = MONTHS.findIndex((x) => parts[1].toLowerCase().startsWith(x.toLowerCase())) + 1;
  else return null;
  return y ? isoOf(Number(parts[0]), m, y) : null;
}

/** Message used wherever a typed date can't be read (never ignored silently — round 18 #3). */
export const DATE_HINT = "Date must look like 05/Mar/26";
/** The same, after a field name: "Target date: date must look like 05/Mar/26". */
export const DATE_HINT_ROW = "date must look like 05/Mar/26";
